import Decimal from 'decimal.js';
import { parseCsv } from './parseCsv';
import { convertUsdToEur } from './eurFx';
import type { LedgerEvent } from './types';

export type DexScreenerPriceMethod = 'usd_over_qty' | 'mantissa_exp';

export type DexScreenerParsedRow = {
  txHashLower: string;
  tsMs: number;
  side: string;
  priceUsd: Decimal;
  priceMethod: DexScreenerPriceMethod;
};

export type ParseDexScreenerCsvResult = {
  rows: DexScreenerParsedRow[];
  skipped: number;
  warnings: string[];
};

const MONTHS: Record<string, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

function normalizeHeaderCell(s: string): string {
  return String(s)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function isLikelyHeaderRow(cells: string[]): boolean {
  const a = (cells[0] || '').trim().toLowerCase();
  if (a === 'date') {
    const rest = cells.map((c) => normalizeHeaderCell(c ?? ''));
    if (rest.some((x) => x === 'usd')) return true;
  }
  return (
    a.includes('chakra') ||
    a.includes('custom-') ||
    a.startsWith('"custom-')
  );
}

/** Row like `DATE / TYPE / USD / BUTT / WCRO / PRICE / mantissa` (new scrapes) vs older Chakra-only header. */
function findDexScreenerHeaderRowIndex(grid: string[][]): number {
  for (let i = 0; i < Math.min(grid.length, 25); i++) {
    const row = grid[i];
    if (!row?.length) continue;
    const lower = row.map((c) => normalizeHeaderCell(c ?? ''));
    const hasDate = lower[0] === 'date';
    const hasUsd = lower.some((x) => x === 'usd');
    if (hasDate && hasUsd) return i;
    if (hasUsd && lower.some((x) => x.includes('mantissa'))) return i;
  }
  return -1;
}

export type DexScreenerColMap = {
  dateCol: number;
  sideCol: number;
  usdIndex: number;
  qtyIndex: number;
  priceIndex: number;
  expIndex: number;
  txCol: number;
};

/**
 * Map columns from a header row like:
 * `DATE, TYPE, USD, BUTT, WCRO, PRICE (USD), mantissa` (+ explorer URL column in data rows).
 * Prefers **USD** and token qty **between USD and WCRO** (or column matching `assetSymbol`).
 */
export function buildColMapFromDexScreenerHeader(
  headerRow: string[],
  assetSymbol?: string
): Omit<DexScreenerColMap, 'txCol'> | null {
  const h = headerRow.map((c) => normalizeHeaderCell(c ?? ''));
  const sym = (assetSymbol || '').trim().toUpperCase();

  const dateCol = h.findIndex((x) => x === 'date');
  const sideCol = h.findIndex((x) => x === 'type' || x === 'side');
  const usdIndex = h.findIndex((x) => x === 'usd');
  const wcroIndex = h.findIndex((x) => x === 'wcro');
  const priceIndex = h.findIndex((x) => x.includes('price'));
  const expIndex = h.findIndex((x) => x === 'mantissa' || x.includes('mantissa'));

  let qtyIndex = -1;
  if (sym) {
    qtyIndex = h.findIndex((x) => x === sym.toLowerCase());
  }
  if (qtyIndex < 0 && usdIndex >= 0 && wcroIndex === usdIndex + 2) {
    /** `USD | BUTT | WCRO` layout */
    qtyIndex = usdIndex + 1;
  }
  if (qtyIndex < 0 && usdIndex >= 0 && wcroIndex > usdIndex) {
    /** First column strictly between USD and WCRO */
    if (wcroIndex === usdIndex + 1) qtyIndex = -1;
    else qtyIndex = usdIndex + 1;
  }

  if (usdIndex < 0 || qtyIndex < 0 || priceIndex < 0 || expIndex < 0) return null;

  return {
    dateCol: dateCol >= 0 ? dateCol : 0,
    sideCol: sideCol >= 0 ? sideCol : 1,
    usdIndex,
    qtyIndex,
    priceIndex,
    expIndex,
  };
}

/** DexScreener export dates look like `Apr 11 06:53:12 PM` (no year). */
export function parseDexScreenerDisplayDate(s: string, yearUtc: number): number | null {
  const t = s.trim();
  const m = t.match(
    /^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{1,2}):(\d{2}):(\d{2})\s+(AM|PM)$/i
  );
  if (!m) return null;
  const mon = MONTHS[m[1]!];
  if (mon == null) return null;
  const day = parseInt(m[2]!, 10);
  let hour = parseInt(m[3]!, 10);
  const min = parseInt(m[4]!, 10);
  const sec = parseInt(m[5]!, 10);
  const ap = m[6]!.toUpperCase();
  if (ap === 'PM' && hour < 12) hour += 12;
  if (ap === 'AM' && hour === 12) hour = 0;
  return Date.UTC(yearUtc, mon, day, hour, min, sec);
}

export function txHashFromExplorerUrl(cell: string): string | null {
  const m = String(cell).match(/\/tx\/(0x[a-fA-F0-9]{64})/i);
  return m ? m[1]!.toLowerCase() : null;
}

function parseLooseNumber(cell: string): Decimal | null {
  const t = String(cell).replace(/,/g, '').replace(/^\$/, '').trim();
  if (!t) return null;
  try {
    const d = new Decimal(t);
    return d.isFinite() ? d : null;
  } catch {
    return null;
  }
}

/**
 * Prefer **USD total ÷ token qty** when both exist (matches on-chain notional).
 * Otherwise: `mantissa * 10^(-exponent)` where mantissa is the `$…` price cell and the next column is the exponent
 * (DexScreener subscript / leading-zero convention).
 */
export function dexScreenerUsdPerTokenFromCells(
  cols: string[],
  opts: { qtyIndex: number; usdIndex: number; priceIndex: number; expIndex: number }
): { priceUsd: Decimal | null; method: DexScreenerPriceMethod } {
  const qty = parseLooseNumber(cols[opts.qtyIndex] ?? '');
  const usdTotal = parseLooseNumber(cols[opts.usdIndex] ?? '');
  if (qty != null && usdTotal != null && qty.gt(0)) {
    return { priceUsd: usdTotal.div(qty), method: 'usd_over_qty' };
  }

  const mantissa = parseLooseNumber(cols[opts.priceIndex] ?? '');
  const expRaw = (cols[opts.expIndex] ?? '').trim();
  const exp = parseInt(expRaw, 10);
  if (mantissa != null && Number.isFinite(exp)) {
    const priceUsd = mantissa.mul(new Decimal(10).pow(-exp));
    if (priceUsd.gt(0)) return { priceUsd, method: 'mantissa_exp' };
  }
  return { priceUsd: null, method: 'mantissa_exp' };
}

function detectColumnIndices(sampleRows: string[][]): {
  txCol: number;
  qtyIndex: number;
  usdIndex: number;
  priceIndex: number;
  expIndex: number;
} | null {
  let txCol = -1;
  for (const r of sampleRows) {
    for (let i = 0; i < r.length; i++) {
      if (/\/tx\/0x[a-fA-F0-9]{64}/i.test(r[i] || '')) {
        txCol = i;
        break;
      }
    }
    if (txCol >= 0) break;
  }
  if (txCol < 0) return null;
  /** Example export: … qty, USD, $price, exp, … tx (last). */
  if (txCol >= 9) {
    return {
      txCol,
      qtyIndex: txCol - 6,
      usdIndex: txCol - 5,
      priceIndex: txCol - 4,
      expIndex: txCol - 3,
    };
  }
  /** Minimal guess when tx is not last */
  return {
    txCol,
    qtyIndex: Math.max(0, txCol - 6),
    usdIndex: Math.max(0, txCol - 5),
    priceIndex: Math.max(0, txCol - 4),
    expIndex: Math.max(0, txCol - 3),
  };
}

function findTxColumnIndex(sampleRows: string[][]): number {
  for (const r of sampleRows) {
    for (let i = 0; i < r.length; i++) {
      if (/\/tx\/0x[a-fA-F0-9]{64}/i.test(r[i] || '')) return i;
    }
  }
  return -1;
}

/**
 * Parse a DexScreener (or similar) scraped CSV. One file usually covers **one** token.
 * Dates without a year use `defaultYear` (UTC), e.g. the reporting year you select in the UI.
 *
 * When the sheet has a header row (`DATE`, `USD`, `BUTT`, `WCRO`, …), columns are mapped by name so
 * **USD notional** is not confused with **WCRO** (older logic used fixed offsets from the tx column).
 */
export function parseDexScreenerCsv(
  text: string,
  options: { defaultYear: number; assetSymbol?: string }
): ParseDexScreenerCsvResult {
  const grid = parseCsv(text.replace(/^\uFEFF/, '').trim());
  const warnings: string[] = [];
  let skipped = 0;

  const headerIdx = findDexScreenerHeaderRowIndex(grid);
  let dataRows =
    headerIdx >= 0
      ? grid.slice(headerIdx + 1).filter((row) => row.length > 0 && !isLikelyHeaderRow(row))
      : grid.filter((row) => row.length > 0 && !isLikelyHeaderRow(row));

  const sample = dataRows.slice(0, 40);
  const txCol = findTxColumnIndex(sample);
  if (txCol < 0) {
    return {
      rows: [],
      skipped: dataRows.length,
      warnings: ['No transaction URL column found (expected a cell with /tx/0x…).'],
    };
  }

  let colMap: DexScreenerColMap;
  const fromHeader =
    headerIdx >= 0
      ? buildColMapFromDexScreenerHeader(grid[headerIdx]!, options.assetSymbol)
      : null;

  if (fromHeader != null) {
    colMap = { ...fromHeader, txCol };
  } else {
    const idx = detectColumnIndices(sample);
    if (idx == null) {
      return {
        rows: [],
        skipped: dataRows.length,
        warnings: ['Could not detect DexScreener columns (no header match and offset heuristic failed).'],
      };
    }
    colMap = {
      dateCol: 0,
      sideCol: 1,
      txCol: idx.txCol,
      qtyIndex: idx.qtyIndex,
      usdIndex: idx.usdIndex,
      priceIndex: idx.priceIndex,
      expIndex: idx.expIndex,
    };
  }

  const { dateCol, sideCol, qtyIndex, usdIndex, priceIndex, expIndex } = colMap;

  const byTx = new Map<string, DexScreenerParsedRow>();

  for (const row of dataRows) {
    if (row.length <= txCol) {
      skipped++;
      continue;
    }
    const txHash = txHashFromExplorerUrl(row[txCol] || '');
    if (!txHash) {
      skipped++;
      continue;
    }
    const tsMs = parseDexScreenerDisplayDate(row[dateCol] || '', options.defaultYear);
    if (tsMs == null) {
      skipped++;
      warnings.push(`Bad date cell: ${String(row[dateCol]).slice(0, 40)}…`);
      continue;
    }
    const { priceUsd, method } = dexScreenerUsdPerTokenFromCells(row, {
      qtyIndex,
      usdIndex,
      priceIndex,
      expIndex,
    });
    if (priceUsd == null || !priceUsd.gt(0)) {
      skipped++;
      continue;
    }
    const side = (row[sideCol] || '').trim();
    byTx.set(txHash, {
      txHashLower: txHash,
      tsMs,
      side,
      priceUsd,
      priceMethod: method,
    });
  }

  return {
    rows: Array.from(byTx.values()),
    skipped,
    warnings: warnings.slice(0, 20),
  };
}

export function dexScreenerRowsToPriceMap(rows: DexScreenerParsedRow[]): Map<string, Decimal> {
  const m = new Map<string, Decimal>();
  for (const r of rows) {
    m.set(r.txHashLower, r.priceUsd);
  }
  return m;
}

const FLAGS_TO_CLEAR = new Set([
  'unresolved_token_price',
  'missing_cost_basis',
  'no_cro_swap_ratio',
]);

/**
 * Apply DexScreener-derived **USD per token** (converted with ECB USD→EUR at event time) to matching
 * Cronos ledger rows (same `ref` tx hash and asset symbol). Typical use: meme-token swaps where the explorer
 * pipeline left EUR empty.
 */
export async function applyDexScreenerUsdToLedgerEvents(
  events: LedgerEvent[],
  options: {
    priceByTxHash: Map<string, Decimal>;
    assetSymbol: string;
    /** When true, also fills `acquisition` rows (cost basis). Default true. */
    applyToAcquisitions?: boolean;
    /** Overwrite existing EUR when true. Default: only when missing / zero / missing_proceeds_review. */
    force?: boolean;
  }
): Promise<{ events: LedgerEvent[]; applied: number }> {
  const sym = options.assetSymbol.trim().toUpperCase();
  if (!sym || options.priceByTxHash.size === 0) {
    return { events, applied: 0 };
  }
  const applyAcq = options.applyToAcquisitions !== false;
  let applied = 0;
  const out: LedgerEvent[] = [];

  for (const ev of events) {
    if (ev.source !== 'cronos') {
      out.push(ev);
      continue;
    }
    if (ev.kind !== 'disposal' && ev.kind !== 'acquisition') {
      out.push(ev);
      continue;
    }
    if (ev.kind === 'acquisition' && !applyAcq) {
      out.push(ev);
      continue;
    }
    if (ev.asset.toUpperCase() !== sym) {
      out.push(ev);
      continue;
    }
    const ref = (ev.ref || '').toLowerCase();
    if (!ref.startsWith('0x') || ref.length !== 66) {
      out.push(ev);
      continue;
    }
    const priceUsd = options.priceByTxHash.get(ref);
    if (priceUsd == null || !priceUsd.gt(0)) {
      out.push(ev);
      continue;
    }

    const shouldApply =
      options.force === true ||
      ev.eurValue == null ||
      (ev.eurValue && ev.eurValue.abs().lte(0)) ||
      ev.valuationFlag === 'missing_proceeds_review';

    if (!shouldApply) {
      out.push(ev);
      continue;
    }

    const date = new Date(ev.ts);
    const usdNotional = ev.amount.abs().mul(priceUsd);
    let eur: Decimal;
    try {
      eur = await convertUsdToEur(usdNotional, date);
    } catch {
      out.push(ev);
      continue;
    }

    const reviewFlags = (ev.reviewFlags ?? []).filter((f) => !FLAGS_TO_CLEAR.has(f));
    const extra = `DexScreener CSV: USD/token × amount → ECB USD→EUR (${priceUsd.toFixed(12)} USD/unit).`;
    const disposalPatch =
      ev.kind === 'disposal'
        ? {
            proceedsDerivation: 'dexscreener_csv' as const,
            valuationFlag: 'normal' as const,
          }
        : {};
    out.push({
      ...ev,
      ...disposalPatch,
      eurValue: eur,
      cronosPriceHint: undefined,
      reviewFlags,
      explanation: ev.explanation ? `${ev.explanation} ${extra}` : extra,
    });
    applied++;
  }

  return { events: out, applied };
}

export type DexScreenerLibraryApplyEntry = {
  priceByTxHash: Map<string, Decimal>;
  assetSymbol: string;
};

/**
 * Apply several DexScreener exports in sequence (one map per token symbol). Later entries override
 * the same tx hash only if earlier passes did not already price that row.
 */
export async function applyDexScreenerLibraryToLedgerEvents(
  events: LedgerEvent[],
  entries: DexScreenerLibraryApplyEntry[],
  options?: { applyToAcquisitions?: boolean; force?: boolean }
): Promise<{ events: LedgerEvent[]; applied: number; perSymbol: Record<string, number> }> {
  let out = events;
  let applied = 0;
  const perSymbol: Record<string, number> = {};

  for (const ent of entries) {
    const sym = ent.assetSymbol.trim().toUpperCase();
    if (!sym || ent.priceByTxHash.size === 0) continue;
    const r = await applyDexScreenerUsdToLedgerEvents(out, {
      priceByTxHash: ent.priceByTxHash,
      assetSymbol: sym,
      applyToAcquisitions: options?.applyToAcquisitions,
      force: options?.force,
    });
    out = r.events;
    perSymbol[sym] = (perSymbol[sym] ?? 0) + r.applied;
    applied += r.applied;
  }

  return { events: out, applied, perSymbol };
}
